import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import "./i18n";

// Import components
import Header from "./components/Header";
import Hero from "./components/Hero";
import About from "./components/About";
import Partners from "./components/Partners";
import Footer from "./components/Footer";
import MediaPage from "./pages/MediaPage";
import ContactPage from "./pages/ContactPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import ResourcesPage from "./pages/ResourcesPage";
import NewsPage from "./pages/NewsPage";
import EventsPage from "./pages/EventsPage";
import TrainingsPage from "./pages/TrainingsPage";
import TrainingDetailPage from "./pages/TrainingDetailPage";
import NewslettersPage from "./pages/NewslettersPage";

// Scroll to top component with page view tracking for SPA
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track page view on route change with explicit hit type (GA4 event)
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: pathname,
        page_title: document.title,
        page_location: window.location.origin + pathname,
      });
      // Also update config so subsequent events use this page context
      window.gtag('config', 'G-QL75YHYD9E', {
        page_path: pathname,
      });
    } else {
      const measurementId = process.env.REACT_APP_GA_TRACKING_ID;
      if (measurementId) {
        ReactGA.send({ hitType: 'page-view', page: pathname, title: document.title });
      }
    }
  }, [pathname]);

  return null;
};

// Main Home Page Component
const HomePage = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle scroll to section
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("section[id]");
      let currentSection = "home";

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 100) {
          currentSection = section.getAttribute("id");
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      {/* Main Content */}
      <main className="pt-16 container mx-auto px-4">
        {/* Hero Section */}
        <Hero />

        {/* About Section */}
        <About />

        {/* Partners Section */}
        <Partners />
      </main>

      {/* Footer */}
      <Footer
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />
    </div>
  );
};

export default function App() {
  // Initialize Google Analytics
  useEffect(() => {
    const measurementId = process.env.REACT_APP_GA_TRACKING_ID;
    if (measurementId) {
      ReactGA.initialize(measurementId);
    }
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/media" element={<MediaPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/activities" element={<ActivitiesPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news-letters" element={<NewslettersPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/trainings" element={<TrainingsPage />} />
        <Route path="/trainings/:slug" element={<TrainingDetailPage />} />
      </Routes>
    </Router>
  );
}
